import { useState } from "react";

const contactEmail = "hamza.ouabiba@gmail.com";

const contactLinks = [
  {
    key: "email",
    href: `mailto:${contactEmail}?subject=Portfolio%20contact`,
    value: contactEmail,
  },
  {
    key: "github",
    href: "https://github.com/hamza-ouabiba",
    value: "github.com/hamza-ouabiba",
    external: true,
  },
];

const initialForm = {
  name: "",
  email: "",
  subject: "",
  message: "",
};

export default function ContactPage({ t, copy }) {
  const [form, setForm] = useState(initialForm);

  const updateField = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const subject = form.subject || "Portfolio contact";
    const body = [
      `Name: ${form.name}`,
      `Email: ${form.email}`,
      "",
      form.message,
    ].join("\n");

    const gmailUrl = new URL("https://mail.google.com/mail/");
    gmailUrl.searchParams.set("view", "cm");
    gmailUrl.searchParams.set("fs", "1");
    gmailUrl.searchParams.set("to", contactEmail);
    gmailUrl.searchParams.set("su", subject);
    gmailUrl.searchParams.set("body", body);

    window.open(gmailUrl.toString(), "_blank", "noopener,noreferrer");
  };

  return (
    <section>
      <h2 style={{ fontSize: 20, fontWeight: 400, marginBottom: 16 }}>
        {copy.contact}
      </h2>

      <p style={{ color: t.muted, fontSize: 15, marginBottom: 28 }}>
        {copy.contactIntro}
      </p>

      <div style={{ borderTop: `1px solid ${t.borderLight}` }}>
        {contactLinks.map((item) => (
          <ContactRow
            key={item.key}
            label={item.key === "email" ? copy.contactEmailLabel : copy.contactGithubLabel}
            value={item.value}
            action={item.key === "email" ? copy.contactEmailAction : copy.contactGithubAction}
            href={item.href}
            external={item.external}
            t={t}
          />
        ))}

        <InfoRow
          label={copy.contactResponseTimeLabel}
          value={copy.contactResponseTime}
          t={t}
        />
      </div>

      <form onSubmit={handleSubmit} style={formStyle(t)}>
        <h3 style={{ color: t.text, fontSize: 16, fontWeight: 400, margin: "0 0 18px" }}>
          {copy.contactFormTitle}
        </h3>

        <div style={fieldGridStyle}>
          <Field
            label={copy.contactNameLabel}
            name="name"
            value={form.name}
            onChange={updateField("name")}
            placeholder={copy.contactNamePlaceholder}
            required
            t={t}
          />

          <Field
            label={copy.contactEmailLabel}
            name="email"
            type="email"
            value={form.email}
            onChange={updateField("email")}
            placeholder={copy.contactEmailPlaceholder}
            required
            t={t}
          />
        </div>

        <Field
          label={copy.contactSubjectLabel}
          name="subject"
          value={form.subject}
          onChange={updateField("subject")}
          placeholder={copy.contactSubjectPlaceholder}
          required
          t={t}
        />

        <Field
          as="textarea"
          label={copy.contactMessageLabel}
          name="message"
          value={form.message}
          onChange={updateField("message")}
          placeholder={copy.contactMessagePlaceholder}
          required
          t={t}
        />

        <button type="submit" style={buttonStyle(t)}>
          {copy.contactSubmit} -&gt;
        </button>
      </form>
    </section>
  );
}

function Field({ as, label, t, ...props }) {
  const Component = as || "input";

  return (
    <label style={{ display: "block", marginBottom: 16 }}>
      <span style={labelStyle(t)}>{label}</span>
      <Component
        {...props}
        style={{
          background: t.surface,
          border: `1px solid ${t.border}`,
          borderRadius: 8,
          boxSizing: "border-box",
          color: t.text,
          display: "block",
          font: "inherit",
          fontSize: 15,
          lineHeight: 1.5,
          minHeight: as === "textarea" ? 140 : undefined,
          outline: "none",
          padding: "10px 12px",
          resize: as === "textarea" ? "vertical" : undefined,
          width: "100%",
        }}
      />
    </label>
  );
}

function ContactRow({ label, value, action, href, external, t }) {
  return (
    <div style={rowStyle(t)}>
      <div>
        <div style={labelStyle(t)}>{label}</div>
        <a
          href={href}
          target={external ? "_blank" : undefined}
          rel={external ? "noreferrer" : undefined}
          style={{
            color: t.text,
            fontSize: 15,
            textDecoration: "none",
            wordBreak: "break-word",
          }}
        >
          {value}
        </a>
      </div>

      <a
        href={href}
        target={external ? "_blank" : undefined}
        rel={external ? "noreferrer" : undefined}
        style={actionStyle(t)}
      >
        {action} -&gt;
      </a>
    </div>
  );
}

function InfoRow({ label, value, t }) {
  return (
    <div style={rowStyle(t)}>
      <div>
        <div style={labelStyle(t)}>{label}</div>
        <div style={{ color: t.muted, fontSize: 15 }}>{value}</div>
      </div>
    </div>
  );
}

function rowStyle(t) {
  return {
    borderBottom: `1px solid ${t.borderLight}`,
    display: "flex",
    gap: 20,
    justifyContent: "space-between",
    padding: "18px 0",
  };
}

function labelStyle(t) {
  return {
    color: t.dim,
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: 12,
    marginBottom: 4,
  };
}

function actionStyle(t) {
  return {
    alignSelf: "center",
    color: t.link,
    flexShrink: 0,
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: 12,
    textDecoration: "none",
  };
}

function formStyle(t) {
  return {
    borderTop: `1px solid ${t.borderLight}`,
    marginTop: 34,
    paddingTop: 28,
  };
}

const fieldGridStyle = {
  display: "grid",
  gap: 14,
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
};

function buttonStyle(t) {
  return {
    background: t.text,
    border: `1px solid ${t.text}`,
    borderRadius: 8,
    color: t.bg,
    cursor: "pointer",
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: 12,
    padding: "10px 14px",
  };
}
